import React, { useEffect, useState } from "react";
import ReactPDF, {
	Page,
	Text,
	View,
	Document,
	StyleSheet,
	PDFViewer,
	Font,
} from "@react-pdf/renderer";
import { useSearchParams } from "react-router-dom";
import actionPlansService from "services/ActionPlansService";
import objectiveActionPlansService from "services/ObjectiveActionPlansService";
import companyService from "services/CompanyService";
import moment from "moment";

import FontRobotoRegular from "../../../assets/fonts/roboto/Roboto-Regular.ttf";
import FontRobotoBold from "../../../assets/fonts/roboto/Roboto-Bold.ttf";
import FontRobotoItalic from "../../../assets/fonts/roboto/Roboto-Italic.ttf";
import Loading from "components/Common/Loading";

Font.register({
	family: "Roboto",
	fonts: [
		{ src: FontRobotoRegular },
		{ src: FontRobotoBold, fontWeight: "bold" },
		{ src: FontRobotoItalic, fontStyle: "italic" },
	],
});

const styles = StyleSheet.create({
	page: {
		padding: 30,
	},
	title: {
		fontSize: 12,
		fontFamily: "Roboto", // Fonte regular
	},
	titleBold: {
		fontSize: 12,
		fontFamily: "Roboto",
		fontWeight: "bold", // Fonte em negrito
	},
	space: {
		marginTop: 10,
	},
	horizontal_line: {
		borderBottomWidth: 1,
		borderBottomColor: "#000",
		marginVertical: 10,
	},
	header: {
		borderTop: "none",
	},
	table: {
		width: "100%",
		borderStyle: "solid",
		borderWidth: 1,
		borderColor: "#EEE",
	},
	row: {
		display: "flex",
		flexDirection: "row",
		borderTop: "1px solid #EEE",
		paddingTop: 3,
		paddingBottom: 3,
		fontSize: 10,
	},
	headerRow: {
		display: "flex",
		flexDirection: "row",
		backgroundColor: "#EEE",
		paddingTop: 3,
		paddingBottom: 3,
		fontSize: 10,
	},
	row1: {
		width: "40%",
		padding: 5,
	},
	row2: {
		width: "15%",
		padding: 5,
	},
	row3: {
		width: "15%",
		padding: 5,
	},
	row4: {
		width: "15%",
		padding: 5,
	},
	row4final: {
		width: "30%",
		padding: 5,
	},
	row5: {
		width: "15%",
		padding: 5,
	},
	noActionsText: {
		fontSize: 10,
		fontFamily: "Roboto",
		color: "gray",
	},
	noObjectivesText: {
		fontSize: 10,
		fontFamily: "Roboto",
		color: "gray",
	},
});

function getWindowDimensions() {
	const { innerWidth: width, innerHeight: height } = window;
	return {
		width,
		height,
	};
}

function useWindowDimensions() {
	const [windowDimensions, setWindowDimensions] = useState(
		getWindowDimensions(),
	);

	useEffect(() => {
		function handleResize() {
			setWindowDimensions(getWindowDimensions());
		}

		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	return windowDimensions;
}

const Infos = ({ company, action_plan, objectives_action_plan }) => {
	const formatarParaReais = (valor) => {
		return new Intl.NumberFormat("pt-BR", {
			style: "currency",
			currency: "BRL",
		}).format(valor);
	};

	return (
		<Document>
			<Page size="A4" style={styles.page}>
				<Text style={styles.title}>
					Plano de Ação - {company?.name}
				</Text>
				<Text style={styles.space} />
				<Text style={styles.title}>Diretriz</Text>
				<Text style={styles.space} />
				<Text style={styles.titleBold}>{action_plan?.guideline}</Text>

				<Text style={styles.space} />

				<View style={styles.table}>
					<View style={styles.headerRow}>
						<Text style={styles.row1}>Departamento</Text>
						<Text style={styles.row2}>Aprovador</Text>
						<Text style={styles.row3}>Responsável</Text>
						<Text style={styles.row4final}>Prazo Final</Text>
					</View>
					<View style={styles.row}>
						<Text style={styles.row1}>
							{action_plan?.organization?.name}
						</Text>
						<Text style={styles.row2}>
							{action_plan?.approving?.name}
						</Text>
						<Text style={styles.row2}>
							{action_plan?.responsible?.name}
						</Text>
						<Text style={styles.row4final}>
							{moment(action_plan?.deadline).format("DD/MM/YYYY")}
						</Text>
					</View>
				</View>

				<Text style={styles.space} />
				<Text style={styles.space} />
				<Text style={styles.titleBold}>Objetivos</Text>

				{objectives_action_plan?.length > 0 ? (
					objectives_action_plan.map((objective, index) => (
						<View key={index}>
							<Text style={styles.horizontal_line} />
							<Text style={styles.space} />
							<Text style={styles.titleBold}>
								{objective?.objective} | Prazo:{" "}
								{moment(objective?.deadline).format(
									"DD/MM/YYYY",
								)}
							</Text>
							<Text style={styles.space} />

							{objective?.actions?.length > 0 ? (
								<View style={styles.table}>
									<View style={styles.headerRow}>
										<Text style={styles.row1}>Ação</Text>
										<Text style={styles.row2}>Prazo</Text>
										<Text style={styles.row3}>
											Responsável
										</Text>
										<Text style={styles.row4}>Status</Text>
										<Text style={styles.row5}>Custo</Text>
									</View>

									{objective.actions.map(
										(action, actionIndex) => (
											<View
												style={styles.row}
												key={actionIndex}
											>
												<Text style={styles.row1}>
													{action?.action}
												</Text>
												<Text style={styles.row2}>
													{moment(
														action?.deadline,
													).format("DD/MM/YYYY")}
												</Text>
												<Text style={styles.row3}>
													{action?.responsible?.name}
												</Text>
												<Text style={styles.row4}>
													{action?.status}
												</Text>
												<Text style={styles.row5}>
													{formatarParaReais(
														Number(action?.cost),
													)}
												</Text>
											</View>
										),
									)}
								</View>
							) : (
								<Text style={styles.noActionsText}>
									Nenhuma ação disponível
								</Text>
							)}
						</View>
					))
				) : (
					<Text style={styles.noObjectivesText}>
						Nenhum objetivo disponível
					</Text>
				)}
			</Page>
		</Document>
	);
};

const PrintPdf = () => {
	const { height } = useWindowDimensions();
	const [queryParameters] = useSearchParams();
	const planId = queryParameters.get("id");
	const company_id = localStorage.getItem("company_id");

	const [company, setCompany] = useState(null);
	const [actionPlan, setActionPlan] = useState(null);
	const [objectivesActionPlan, setObjectivesActionPlan] = useState([]);
	const [loading, setIsLoading] = useState(true);

	const getCompany = async () => {
		setCompany(await companyService.getById(company_id));
	};

	const getActionPlan = async () => {
		setActionPlan(await actionPlansService.getById(planId));
	};

	const getObjectivesActionPlan = async () => {
		setObjectivesActionPlan(
			await objectiveActionPlansService.get({
				action_plans_id: planId,
			}),
		);
	};

	const getAll = async () => {
		await getCompany();
		await getActionPlan();
		await getObjectivesActionPlan();
		setIsLoading(false);
	};

	useEffect(() => {
		if (company_id) {
			getAll();
		}
	}, [company_id]);

	return (
		<>
			<Loading loading={loading} />
			{!loading && (
				<PDFViewer width="100%" height={height - 7}>
					<Infos
						company={company}
						action_plan={actionPlan}
						objectives_action_plan={objectivesActionPlan}
					/>
				</PDFViewer>
			)}
		</>
	);
};

export default PrintPdf;
